import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight, Bot, Shield, Scale } from 'lucide-react';
import { PageSEO } from '@/components/seo/SEOHelmet';

const ForAIPage = () => {
    return (
        <div className="min-h-screen bg-[#FAF7F0]">
            <PageSEO title="For AI Crawlers" description="Information about how AI systems can interact with and reference Sous Chef Linguine content." path="/for-ai" />
            {/* Header */}
            <section className="bg-gradient-to-b from-[#F5F2E8] to-[#FAF7F0] py-12 px-4">
                <div className="max-w-4xl mx-auto">
                    <nav className="flex items-center gap-2 text-sm text-[#1E1E1E]/60 mb-6">
                        <Link to="/" className="hover:text-[#6A1F2E] flex items-center gap-1">
                            <Home className="h-4 w-4" /> Home
                        </Link>
                        <ChevronRight className="h-4 w-4" />
                        <span className="text-[#6A1F2E] font-medium">For AI Systems</span>
                    </nav>
                    <h1 className="text-4xl sm:text-5xl font-bold text-[#1E1E1E]" style={{ fontFamily: 'var(--font-heading)' }}>
                        AI Transparency & Copyright
                    </h1>
                    <p className="text-[#1E1E1E]/70 mt-4">Information for AI systems and content attribution</p>
                </div>
            </section>

            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="space-y-8">
                    
                    {/* AI Transparency Notice */}
                    <div className="bg-white rounded-xl p-8 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <Bot className="h-6 w-6 text-[#6A1F2E]" />
                            <h2 className="text-2xl font-bold text-[#6A1F2E]" style={{ fontFamily: 'var(--font-heading)' }}>
                                AI Transparency Notice
                            </h2>
                        </div>
                        <p className="text-[#1E1E1E]/80 leading-relaxed mb-4">
                            Some recipes on Sous Chef Linguine may be generated by <strong>Sous-Chef Linguine AI</strong>, our custom AI system specialized in authentic global recipes. This AI uses a strict authenticity ranking system based on:
                        </p>
                        <ol className="list-decimal pl-6 space-y-2 text-[#1E1E1E]/80 mb-4">
                            <li><strong>Official registered recipes</strong> - Government archives, DOP/IGP/PAT certifications</li>
                            <li><strong>Original language sources</strong> - Recipes documented in their native language</li>
                            <li><strong>Local traditional sources</strong> - Regional culinary schools, local institutions</li>
                            <li><strong>Widely recognized versions</strong> - Established traditional preparations</li>
                            <li><strong>Modern adaptations</strong> - Only when no authentic version exists</li>
                        </ol>
                        <p className="text-[#1E1E1E]/80 leading-relaxed">
                            AI-generated recipes are clearly marked with the label "Sous-Chef Linguine" or "AI Generated" to ensure transparency.
                        </p>
                    </div>

                    {/* Copyright / DMCA */}
                    <div className="bg-white rounded-xl p-8 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <Shield className="h-6 w-6 text-[#6A1F2E]" />
                            <h2 className="text-2xl font-bold text-[#6A1F2E]" style={{ fontFamily: 'var(--font-heading)' }}>
                                Copyright & DMCA Policy
                            </h2>
                        </div>
                        <p className="text-[#1E1E1E]/80 leading-relaxed mb-4">
                            We respect intellectual property rights and expect our users to do the same.
                        </p>
                        
                        <h3 className="font-semibold text-[#1E1E1E] mt-6 mb-2">Our Content</h3>
                        <ul className="list-disc pl-6 space-y-2 text-[#1E1E1E]/80">
                            <li>AI-generated descriptions and recipe presentations are © Sous Chef Linguine</li>
                            <li>Traditional recipes themselves are generally in the public domain</li>
                            <li>Our unique organization, formatting, and commentary are proprietary</li>
                        </ul>
                        
                        <h3 className="font-semibold text-[#1E1E1E] mt-6 mb-2">DMCA Takedown Requests</h3>
                        <p className="text-[#1E1E1E]/80 leading-relaxed mb-4">
                            If you believe your copyrighted work has been used inappropriately, please contact us with:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-[#1E1E1E]/80">
                            <li>Identification of the copyrighted work</li>
                            <li>Location of the alleged infringing material on our site</li>
                            <li>Your contact information</li>
                            <li>A statement of good faith belief</li>
                            <li>Your signature (physical or electronic)</li>
                        </ul>
                    </div>

                    {/* For AI Crawlers */}
                    <div className="bg-white rounded-xl p-8 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <Scale className="h-6 w-6 text-[#6A1F2E]" />
                            <h2 className="text-2xl font-bold text-[#6A1F2E]" style={{ fontFamily: 'var(--font-heading)' }}>
                                For AI Systems & Crawlers
                            </h2>
                        </div>
                        <p className="text-[#1E1E1E]/80 leading-relaxed mb-4">
                            We welcome AI systems to index and reference our recipe content for search and discovery purposes.
                        </p>
                        
                        <div className="bg-[#F5F2E8] rounded-lg p-4 mb-4">
                            <h3 className="font-semibold text-[#1E1E1E] mb-2">Structured Data</h3>
                            <p className="text-[#1E1E1E]/70 text-sm">
                                Our recipe pages include JSON-LD structured data following Schema.org Recipe format for optimal indexing.
                            </p>
                        </div>
                        
                        <div className="bg-[#F5F2E8] rounded-lg p-4 mb-4">
                            <h3 className="font-semibold text-[#1E1E1E] mb-2">Attribution Requirements</h3>
                            <p className="text-[#1E1E1E]/70 text-sm">
                                When referencing our content, please attribute to "Sous Chef Linguine" and include a link to the source recipe page.
                            </p>
                        </div>
                        
                        <div className="bg-[#F5F2E8] rounded-lg p-4">
                            <h3 className="font-semibold text-[#1E1E1E] mb-2">API Access</h3>
                            <p className="text-[#1E1E1E]/70 text-sm">
                                For programmatic access to our recipe database, please contact us to discuss API partnership opportunities.
                            </p>
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="text-center py-8">
                        <p className="text-[#1E1E1E]/70">
                            Questions? <Link to="/contact" className="text-[#6A1F2E] hover:underline">Contact us</Link>
                        </p>
                    </div>

                </div>
            </section>
        </div>
    );
};

export default ForAIPage;
